import clsx from 'clsx';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useMeasure } from 'react-use';
import { CompListingsPreview } from 'src/components/Listings/CompListingsPreview';
import { CompListingsPreviewV2 } from 'src/components/Listings/CompListingsPreview/CompListingsPreviewV2';
import { ConnectedComparableListings } from 'src/components/Listings/ConnectedComparableListings';
import { ConnectedComparableListingsV2 } from 'src/components/Listings/ConnectedComparableListings/ConnectedComparableListingsV2';
import { useInventoryEventDetailContext } from 'src/contexts/InventoryEventDetailContext/InventoryEventDetailContext';
import { Stack } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import { getListPriceFromAllinPrice } from 'src/utils/inventoryUtils';
import { AutoPricingInputs, Feature, Listing } from 'src/WebApiController';

import { ListingDetailListingGroupHeader } from './ListingDetailListingGroupHeader';
import { ListingDetailListingHeader } from './ListingDetailListingHeader';
import * as styles from './ListingDetailPanel.css';

type ListingDetailPanelProps = {
  useVerticalView: boolean;
};

export const ListingDetailPanel: React.FC<ListingDetailPanelProps> = ({
  useVerticalView,
}) => {
  const {
    eventListings,
    selectedListing,
    selectedGroup,
    flattenedListingGroup,
    compListings,
    priceCalcQuery,
    listingGroupPriceCalcQuery,
    isLoading,
  } = useInventoryEventDetailContext();

  const hasAutoPricingFeature = useUserHasFeature(Feature.AutoPricing);
  const hasYourListingsInCompsFeature = useUserHasFeature(
    Feature.YourListingsInComps
  );

  const listingWithUpdatedPrice = useMemo(() => {
    if (!selectedListing) return;

    const listPrice = getListPriceFromAllinPrice(
      priceCalcQuery?.data?.websitePrice?.amt,
      selectedListing
    );
    return {
      ...selectedListing,
      // This is to make sure we use the auto-preview price instead of the listing's price when showing comparables
      // And we have to override listPrice because allIn is calculated from listPrice (instead of being read from the listing)
      listPrice: listPrice ?? selectedListing.listPrice,
    };
  }, [priceCalcQuery?.data?.websitePrice?.amt, selectedListing]);

  const [detailsRef, { height }] = useMeasure<HTMLDivElement>();
  const shouldDetailsSideBySide = useMemo(() => height < 750, [height]);

  const { watch } = useFormContext<AutoPricingInputs>();
  const { compListingExcludeFanInventory } = watch();

  const flattenedEventListings = useMemo<Listing[]>(() => {
    return (
      (eventListings?.flatMap(flattenListingGroup) as Listing[])?.filter(
        (l) => l.availQty > 0
      ) ?? []
    );
  }, [eventListings]);

  return (
    <Stack
      ref={detailsRef}
      direction="column"
      width="full"
      height="full"
      gap="m"
      className={clsx(styles.listingDetailPanelContainer, {
        [styles.listingDetailPanelContainerRow]: shouldDetailsSideBySide,
      })}
    >
      {selectedListing && hasAutoPricingFeature ? (
        <ListingDetailListingHeader
          selectedListing={selectedListing}
          useVerticalView={useVerticalView}
          shouldDetailsSideBySide={shouldDetailsSideBySide}
        />
      ) : null}
      {selectedGroup && hasAutoPricingFeature ? (
        <ListingDetailListingGroupHeader
          selectedGroup={selectedGroup}
          flattenedListingGroup={flattenedListingGroup}
          useVerticalView={useVerticalView}
          shouldDetailsSideBySide={shouldDetailsSideBySide}
        />
      ) : null}

      {selectedGroup && hasAutoPricingFeature ? (
        hasYourListingsInCompsFeature ? (
          <CompListingsPreviewV2
            isLoading={isLoading}
            listings={flattenedEventListings}
            listingPriceCalculations={
              listingGroupPriceCalcQuery?.data?.listingPriceCalculations ?? []
            }
            compListings={listingGroupPriceCalcQuery?.data?.compListings ?? []}
            isExcludingFanInventory={Boolean(
              compListingExcludeFanInventory == true
            )}
            selectedListingIds={flattenedListingGroup?.map((l) => l.id) ?? []}
          />
        ) : (
          <CompListingsPreview
            isLoading={isLoading}
            isDetailPanel={true}
            listings={flattenedListingGroup ?? []}
            listingPriceCalculations={
              listingGroupPriceCalcQuery?.data?.listingPriceCalculations ?? []
            }
            compListings={listingGroupPriceCalcQuery?.data?.compListings ?? []}
            sellerCompListings={
              listingGroupPriceCalcQuery?.data?.sellerCompListings ?? []
            }
            isExcludingFanInventory={Boolean(
              compListingExcludeFanInventory == true
            )}
          />
        )
      ) : (
        <div className={styles.connectedComponentsContainer}>
          {hasYourListingsInCompsFeature ? (
            <ConnectedComparableListingsV2
              listings={flattenedEventListings}
              selectedListingId={listingWithUpdatedPrice?.id ?? 0}
              compListings={compListings ?? []}
              sortBy={'price'}
              isSortDescending={false}
              isLoading={isLoading}
              showAutoPriceResults={true}
              isExcludingFanInventory={Boolean(
                compListingExcludeFanInventory == true
              )}
              listingPriceCalc={priceCalcQuery?.data ?? undefined}
            />
          ) : (
            <ConnectedComparableListings
              currentListing={listingWithUpdatedPrice}
              currentListingFromListingGroup={flattenedListingGroup}
              comparableListings={compListings ?? []}
              sortBy={'price'}
              isSortDescending={false}
              isLoading={isLoading}
              listings={undefined}
              showAutoPriceResults={true}
              isExcludingFanInventory={Boolean(
                compListingExcludeFanInventory == true
              )}
              listingPriceCalc={priceCalcQuery?.data ?? undefined}
            />
          )}
        </div>
      )}
    </Stack>
  );
};
